import axios from 'axios';

const API_BASE = (() => {
  let url = process.env.REACT_APP_API_URL || 'http://localhost:5000';
  url = url.replace(/\/api\/?$/, '');
  url = url.replace(/\/$/, '');
  return url;
})();

const api = axios.create({
  baseURL: `${API_BASE}/api`,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('lms-token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err.response?.status === 401) {
      localStorage.removeItem('lms-token');
      localStorage.removeItem('lms-user');
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(err);
  }
);

export const API_BASE_URL = API_BASE;

export const authAPI = {
  login: (data) => api.post('/auth/login', data),
  getMe: () => api.get('/auth/me'),
  updateAdminProfile: (data) => api.put('/auth/profile', data),
  updateUserProfile: (data) => api.put('/auth/user-profile', data),
  changePassword: (data) => api.put('/auth/change-password', data),
};

export const userAPI = {
  getAll: (params) => api.get('/users', { params }),
  getById: (id) => api.get(`/users/${id}`),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  toggleStatus: (id, isActive) => api.patch(`/users/${id}/status`, { isActive }),
};

export const pluginAPI = {
  list: (params) => api.get('/plugins', { params }),
  upload: (formData, onProgress) =>
    api.post('/plugins', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (e) => {
        if (onProgress && e.total) {
          onProgress(Math.round((e.loaded * 100) / e.total));
        }
      },
    }),
  download: (id) =>
    api.get(`/plugins/${id}/download`, { responseType: 'blob' }),
  remove: (id) => api.delete(`/plugins/${id}`),
};

export const licenseAPI = {
  list: (params) => api.get('/licences', { params }),
  get: (id) => api.get(`/licences/${id}`),
  create: (data) => api.post('/licences', data),
  update: (id, data) => api.put(`/licences/${id}`, data),
  toggleStatus: (id, isActive) => api.patch(`/licences/${id}/status`, { isActive }),
  getActiveUsers: () => api.get('/licences/users/active'),
  validate: (data) => api.post('/licences/validate', data),
};

export const adminStatsAPI = {
  get: () => api.get('/admin/stats'),
};

export const userStatsAPI = {
  get: () => api.get('/users/me/stats'),
};

export const userLicencesAPI = {
  getMyLicences: () => api.get('/users/me/licences'),
};

export const postAPI = {
  getAll: (params) => api.get('/posts', { params }),
  getOne: (id) => api.get(`/posts/${id}`),
  create: (formData) => api.post('/posts', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  update: (id, formData) => api.put(`/posts/${id}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  delete: (id) => api.delete(`/posts/${id}`),
};

export const userPostAPI = {
  getAll: (params) => api.get('/user/posts', { params }),
  getOne: (id) => api.get(`/user/posts/${id}`),
};

export const pluginSettingsAPI = {
  get: () => api.get('/plugin-settings'),
  download: (type) => api.get(`/plugin-settings/download/${type}`, { responseType: 'blob' }),
  uploadPdf: (file) => {
    const formData = new FormData();
    formData.append('pdf', file);
    return api.put('/plugin-settings/pdf', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  uploadSet: (file) => {
    const formData = new FormData();
    formData.append('set', file);
    return api.put('/plugin-settings/set', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deletePdf: () => api.delete('/plugin-settings/pdf'),
  deleteSet: () => api.delete('/plugin-settings/set'),
};

export default api;